import { startCase } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EmployeeAvatar from "../../../components/EmployeeAvatar";
import Api from "../../../core/data/redux/utils/apiCall";
import { BASE_URLS, API_URLS } from "../../../core/data/redux/apis/urls";
import { processNocDownloadResponse } from "../../../utils/nocDownloadUtils";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  NOCOutjobRequest,
  setCurrentRequest,
  fetchNOCOutjobById,
} from "../../../core/data/redux/nocOutjobSlice";
import {
  ChatMessage,
  clearError,
  clearMessages,
  clearUploadError,
  fetchMessages,
  fetchUnreadCount,
  fetchChatAttachments,
  sendMessage,
  uploadChatFiles,
} from "../../../core/data/redux/nocOutsideJobChatSlice";
import {
  openChatAttachment,
  openChatAttachmentInExternalView,
  downloadChatAttachment,
  prepareUploadedAttachments,
  formatChatMessageText,
} from "../../../utils/nocChatUtils";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
} from "../../../utils/fileUtils";
import { handleDocumentClick as handleDocumentClickUtil } from "../../../utils/documentUtils";
import { all_routes } from "../../router/all_routes";

interface NOCOutsideJobDetailsProps { }

const NOCOutsideJobDetails = (props: NOCOutsideJobDetailsProps) => {
  const routes = all_routes;
  const { id } = useParams();
  const dispatch = useDispatch<any>();

  // Get data from Redux state
  const { requests, currentRequest, loading } = useSelector(
    (state: any) => state.nocOutjobState
  );
  const employeesState = useSelector((state: any) => state.employeesState);

  // Get chat data from Redux state
  const {
    messages,
    attachments,
    loading: chatLoading,
    unreadCount,
    error: chatError,
    uploading,
    uploadError,
  } = useSelector((state: any) => state.nocChat);

  const [applicationData, setApplicationData] =
    useState<NOCOutjobRequest | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const [currentUserType, setCurrentUserType] = useState<string>("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [isCurrentUserApprover, setIsCurrentUserApprover] =
    useState<boolean>(false);

  const handleDownloadPDF = async () => {
    if (!applicationData || isDownloading) return;
    try {
      setIsDownloading(true);
      const payload = {
        empId: applicationData.empID || applicationData.empId,
        type: "ZNOJ",
        referenceNumber: applicationData.referenceNumber,
      };

      const res = await Api.post(
        API_URLS.NOC.OUTSIDE_JOB_DOWNLOAD,
        payload
      );

      processNocDownloadResponse(res?.data);
    } catch (err: any) {
      console.error("Outside Job NOC Download Error:", err);
      toast.error(err?.message || "Failed to download NOC document.");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    dispatch(getMyProfile({}));
    const user = localStorage.getItem("user");
    const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
    setCurrentUserType(userJson?.role || userJson?.user_type || "");
    setCurrentUserId(userJson?.empId || userJson?.empID || "");
  }, [dispatch]);

  useEffect(() => {
    if (applicationData && currentUserId) {
      const appApprover = String(applicationData.approverId || (applicationData as any).approverUserID || "").replace(/^0+/, "");
      const myId = String(currentUserId).replace(/^0+/, "");
      const isApprover = appApprover === myId || (applicationData.approverId === currentUserId);
      setIsCurrentUserApprover(isApprover);
    }
  }, [applicationData, currentUserId]);

  // Fetch messages and unread count as soon as ID is available
  useEffect(() => {
    const requestId = id ? parseInt(id) : applicationData?.id;
    if (requestId && !isNaN(requestId)) {
      dispatch(fetchMessages({ nocRequestId: requestId }));
      dispatch(fetchUnreadCount(requestId));
    }
  }, [id, applicationData?.id, dispatch]);

  // Load data from Redux state & fetch by ID from API (noc-outjob-request-by-id)
  useEffect(() => {
    if (id) {
      const numId = parseInt(id);
      dispatch(fetchNOCOutjobById(numId))
        .unwrap()
        .then((res: any) => {
          if (res) setApplicationData(res);
        })
        .catch(() => {
          const found = requests.find((req: NOCOutjobRequest) => req.id === numId);
          if (found) setApplicationData(found);
        });
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (currentRequest && currentRequest.id === parseInt(id || "0")) {
      setApplicationData(currentRequest);
    }
  }, [currentRequest, id]);

  // Helper functions
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getDarStatusLabel = (code: string | undefined | null) => {
    if (!code) return "-";
    const status = String(code).trim().toUpperCase();
    const mapping: { [key: string]: string } = {
      N: "No D&AR contemplated against the employee",
      P: "D&AR Pending/ Deemed Suspension /Suspension/ Court Case Pending",
      M: "D&AR completed; Minor penalty has been imposed",
      J: "D&AR completed; Major penalty has been imposed",
    };
    return mapping[status] || code;
  };

  // Helper function to convert numbers to ordinal format
  const getOrdinalNumber = (num: string | number | undefined) => {
    if (!num) return "-";
    const n = typeof num === "string" ? parseInt(num) : num;
    if (isNaN(n)) return num;

    const suffixes = ["th", "st", "nd", "rd"];
    const remainder = n % 100;

    if (remainder >= 11 && remainder <= 13) {
      return n + "th";
    }

    const lastDigit = n % 10;
    const suffix = suffixes[lastDigit] || suffixes[0];
    return n + suffix;
  };

  // Helper function to convert y/n to yes/no
  const formatYesNo = (value: string | undefined) => {
    if (!value) return "-";
    const lowerValue = value.toLowerCase();
    if (lowerValue === "y" || lowerValue === "yes") return "Yes";
    if (lowerValue === "n" || lowerValue === "no") return "No";
    return value;
  };

  const getStatusBadgeClass = (status: string | undefined) => {
    if (!status) return "badge-secondary";
    switch (status.toLowerCase()) {
      case "pending":
      case "submitted":
        return "badge-warning";
      case "approved":
        return "badge-success";
      case "rejected":
        return "badge-danger";
      case "in_progress":
      case "under_review":
        return "badge-info";
      case "draft":
        return "badge-secondary";
      default:
        return "badge-secondary";
    }
  };

  const getNatureBadgeClass = (natureTitle: string | undefined) => {
    if (!natureTitle) return "bg-secondary-transparent text-secondary";

    switch (natureTitle.toLowerCase()) {
      case "government service":
        return "bg-primary-transparent text-primary";
      case "private/corporate service":
        return "bg-success-transparent text-success";
      case "consultancy":
        return "bg-info-transparent text-info";
      case "teaching/academic":
        return "bg-warning-transparent text-warning";
      case "business/entrepreneurship":
        return "bg-danger-transparent text-danger";
      default:
        return "bg-secondary-transparent text-secondary";
    }
  };

  // Get the display value for nature
  const getNatureDisplay = () => {
    if (!applicationData) return "-";

    const natureTitle =
      applicationData.natureOfOutsideJob || applicationData.natureOfOutside;

    if (!natureTitle) return "-";

    // If it's a number/ID and we have natureDetails, use the title
    if (
      /^\d+$/.test(natureTitle) &&
      applicationData.natureDetails?.natureTitle
    ) {
      return applicationData.natureDetails.natureTitle;
    }

    // If it's already a readable title, return it
    if (natureTitle && !/^\d+$/.test(natureTitle)) {
      return natureTitle;
    }

    // Fallback to raw value
    return natureTitle;
  };

  const getRejectionReasonDisplay = (reasonId: string | undefined | null) => {
    if (!reasonId || reasonId.toString().trim() === "0") return "-";
    const id = reasonId.toString().trim();
    const reasonsMap: Record<string, string> = {
      "1": "Incomplete details or documentation",
      "2": "Not eligible as per DMRC policy",
      "3": "Under D&AR/Vigilance proceedings",
      "4": "Other administrative reason"
    };
    return reasonsMap[id] || id;
  };

  const [showChat, setShowChat] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messageInputRef = React.useRef<HTMLInputElement>(null);
  const [isSending, setIsSending] = useState(false);

  // Helper to determine if a message is sent by current logged in user
  const isMyMessage = (msg: ChatMessage): boolean => {
    const userStr = localStorage.getItem("user");
    const userObj = userStr && userStr !== "undefined" ? JSON.parse(userStr) : {};
    const myEmpId = String(userObj?.empId || currentUserId || "").trim().replace(/^0+/, "");
    const senderEmpId = String(msg.senderEmpId || "").trim().replace(/^0+/, "");
    if (myEmpId && senderEmpId) {
      return myEmpId === senderEmpId;
    }
    const isApprover =
      userObj?.role === "APPROVER" ||
      userObj?.user_type === "APPROVER" ||
      isCurrentUserApprover ||
      Boolean(userObj?.approver_level);
    if (isApprover) {
      return msg.senderType === "approver" || msg.senderType === "admin";
    }
    return msg.senderType === "employee";
  };

  // Auto-scroll to bottom of chat when new messages arrive or when chat is opened
  useEffect(() => {
    if (showChat) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages, showChat]);

  // Document request states
  const [showDocumentTypeDialog, setShowDocumentTypeDialog] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
  const [pendingMessage, setPendingMessage] = useState<string>("");

  // Function to detect if message is requesting a document
  const isDocumentRequest = (message: string): boolean => {
    const documentKeywords = [
      "document",
      "documents",
      "file",
      "files",
      "upload",
      "attach",
      "attachment",
      "send",
      "provide",
      "submit",
      "certificate",
      "proof",
      "evidence",
      "scan",
      "copy",
      "copies",
    ];

    const lowerMessage = message.toLowerCase();
    return documentKeywords.some((keyword) => lowerMessage.includes(keyword));
  };

  // Helper function to get the last requested document type from messages
  const getLastRequestedDocumentType = (): string | undefined => {
    // Find the most recent message from approver that has documentType
    const approverMessages = messages
      .filter(
        (msg: ChatMessage) => msg.senderType === "approver" && msg.documentType
      )
      .reverse(); // Get most recent first

    if (approverMessages.length > 0) {
      return approverMessages[0].documentType;
    }

    return undefined;
  };

  // Helper function to convert document type to typeOfAttachedDoc format
  const getTypeOfAttachedDoc = (documentType?: string): string => {
    if (!documentType) return "E"; // Default to 'E' (Others/Education)

    switch (documentType.toLowerCase()) {
      case "education":
        return "E";
      case "advertisement":
        return "A";
      case "others":
      default:
        return "E";
    }
  };

  // Fetch messages and attachments when chat opens (No recurring polling to prevent typing focus loss)
  useEffect(() => {
    const reqId = id ? parseInt(id) : applicationData?.id;
    if (!reqId || isNaN(reqId)) return;

    if (showChat) {
      dispatch(fetchMessages({ nocRequestId: reqId }));
      dispatch(fetchUnreadCount(reqId));
      dispatch(fetchChatAttachments({ nocRequestId: reqId, nocType: "OUTSIDE-JOB" }));
    }
  }, [id, applicationData?.id, showChat, dispatch]);

  const isBoilerplateDocMessage = (text: string) => {
    if (!text) return false;
    const lower = text.trim().toLowerCase();
    return (
      lower === "please share education documents" ||
      lower === "please share advertisement documents" ||
      lower === "please share other documents" ||
      (lower.startsWith("please share") && lower.endsWith("documents"))
    );
  };

  const handleSendMessage = async () => {
    const trimmedMsg = newMessage.trim();
    const isBoilerplate = isBoilerplateDocMessage(trimmedMsg);

    if (!trimmedMsg && selectedFiles.length === 0 && !selectedDocumentType) {
      return;
    }

    // Mobile parity (StartChat.tsx):
    // Message is required ONLY when sending a direct attachment without a document type.
    // If a document type is selected, message is NOT required.
    const isDirectAttachment = !selectedDocumentType && selectedFiles.length > 0;
    if (isDirectAttachment && !trimmedMsg) {
      const errorMsg = "Please add a message along with the attachment.";
      setInputError(errorMsg);
      toast.warning(errorMsg);
      setTimeout(() => messageInputRef.current?.focus(), 100);
      return;
    }

    // If boilerplate doc request text is typed alongside file, clear it
    if (isBoilerplate && selectedFiles.length > 0) {
      setNewMessage("");
    }

    setInputError(null);

    if (!applicationData?.id) {
      return;
    }

    try {
      setIsSending(true);
      let uploadedDocs: any[] = [];
      const requestedDocType = getLastRequestedDocumentType();

      // If files are selected, upload them first
      if (selectedFiles.length > 0) {
        const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
        if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
          uploadedDocs = prepareUploadedAttachments(
            uploadResult.payload,
            selectedFiles,
            selectedDocumentType || requestedDocType
          );
        } else {
          toast.error("Failed to upload attached files. Please try again.");
          return;
        }
      }

      // Format message text with embedded attachment reference for backend @IsNotEmpty validator
      // Never send boilerplate share messages as the payload message with attachments
      let textToEmbed = isBoilerplate ? "" : trimmedMsg;
      if (!textToEmbed && selectedFiles.length === 0 && selectedDocumentType) {
        textToEmbed = `Please share ${selectedDocumentType} Documents`;
      }
      const finalMessage = formatChatMessageText(textToEmbed, uploadedDocs);

      await dispatch(
        sendMessage({
          nocRequestId: applicationData.id,
          message: finalMessage,
          attachments: uploadedDocs,
          documents: uploadedDocs,
          files: selectedFiles,
          documentType: selectedDocumentType || (uploadedDocs.length > 0 ? requestedDocType : undefined),
        })
      ).unwrap();

      setNewMessage("");
      setSelectedFiles([]);
      setSelectedDocumentType("");
      setPendingMessage("");
      setInputError(null);

      // Refresh messages after sending
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      if (uploadedDocs.length > 0) {
        dispatch(fetchChatAttachments({ nocRequestId: applicationData.id, nocType: "OUTSIDE-JOB" }));
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
      const errMsg =
        (typeof error === "string" && error.trim()) ||
        error?.message ||
        error?.error ||
        (Array.isArray(error?.data) && error.data[0]) ||
        "Error sending message. Please try again.";
      toast.error(errMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleDocumentTypeSelection = async () => {
    if (!selectedDocumentType) {
      alert("Please select a document type");
      return;
    }

    setShowDocumentTypeDialog(false);

    if (pendingMessage.trim() && applicationData?.id) {
      try {
        let uploadedDocs: any[] = [];
        if (selectedFiles.length > 0) {
          const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
          if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
            uploadedDocs = prepareUploadedAttachments(
              uploadResult.payload,
              selectedFiles,
              selectedDocumentType
            );
          }
        }

        await dispatch(
          sendMessage({
            nocRequestId: applicationData.id,
            message: pendingMessage,
            attachments: uploadedDocs,
            documentType: selectedDocumentType,
          })
        );

        setNewMessage("");
        setSelectedFiles([]);
        setSelectedDocumentType("");
        setPendingMessage("");

        // Refresh messages after sending
        dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      } catch (error) {
        console.error("Error sending message:", error);
      }
    }
  };

  const handleCancelDocumentType = () => {
    setShowDocumentTypeDialog(false);
    setSelectedDocumentType("");
    setPendingMessage("");
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        // Validate file type
        if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
          errors.push(
            `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(
              ", "
            )}`
          );
          return;
        }

        // Validate file size
        if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
          errors.push(
            `${file.name}: File size too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
          );
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        alert("File validation errors:\n" + errors.join("\n"));
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
        if (isBoilerplateDocMessage(newMessage)) {
          setNewMessage("");
        }
      }
    }

    // Clear the input value to allow selecting the same file again
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) setInputError(null);
      return updated;
    });
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleChatToggle = () => {
    setShowChat(!showChat);
    if (!showChat && applicationData?.id) {
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      dispatch(fetchChatAttachments({ nocRequestId: applicationData.id, nocType: "OUTSIDE-JOB" }));
    } else {
      dispatch(clearMessages());
      // Clear file states when closing chat
      setSelectedFiles([]);
      setUploadingFiles(new Set());
    }
  };

  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    title: string;
    isImage: boolean;
  } | null>(null);

  const formatMessageTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleAttachmentClick = async (rawAttachment: any) => {
    if (!rawAttachment) return;
    await openChatAttachment(rawAttachment, applicationData?.id, "OUTSIDE_JOB", dispatch, attachments);
  };

  const handleDocumentClick = (docParam: any) => {
    handleDocumentClickUtil(docParam, "NOC_OUTJOB");
  };


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!applicationData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">
            <i className="ti ti-alert-circle me-2"></i>
            NOC request not found. Please go back to the main page and try
            again.
          </div>
          <Link to={routes.noc.outsideJob} className="btn btn-primary">
            <i className="ti ti-arrow-left me-2"></i>
            Back to NOC List
          </Link>
        </div>
      </div>
    );
  }

  const natureDisplay = getNatureDisplay();
  const natureBadgeClass = getNatureBadgeClass(natureDisplay);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.outsideJob}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                NOC for Outside Job - {applicationData.referenceNumber}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.outsideJob}>Outside Job</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Application Details
                </li>
              </ol>
            </nav>
          </div>

          {String((applicationData as any)?.requestStatus || "").toUpperCase() === "D" && (
            <button
              className="btn btn-success ms-auto"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              <i className="ti ti-download me-2"></i>
              {isDownloading ? "Downloading..." : "Download Digital NOC"}
            </button>
          )}
        </div>



        {/* Enhanced Chat Section - Moved to top */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Communication</h5>
              {/* 
                Chat Access Logic (matches Mobile):
                - Approver / Admin / HR: can always start/view chat
                - Employee: can view/respond if messages exist (or if approver sent message)
              */}
              {(() => {
                const isApproverOrAdmin =
                  isCurrentUserApprover ||
                  (currentUserType && currentUserType.toUpperCase() !== "EMPLOYEE");
                const hasApproverMessage =
                  messages &&
                  messages.length > 0 &&
                  messages.some(
                    (m: any) =>
                      String(m.senderEmpId || "").replace(/^0+/, "") ===
                      String(applicationData?.approverId || "").replace(/^0+/, "") ||
                      m.senderType === "approver" ||
                      m.senderType === "admin"
                  );
                const hasAnyMessage = messages && messages.length > 0;
                const canChat = isApproverOrAdmin || hasApproverMessage || hasAnyMessage;

                if (!canChat) {
                  return (
                    <div className="text-muted small">
                      <i className="ti ti-info-circle me-1"></i>
                      Chat will be available once the approver initiates communication
                    </div>
                  );
                }

                return (
                  <button
                    className="btn btn-primary btn-sm position-relative"
                    onClick={handleChatToggle}
                  >
                    <i className="ti ti-message me-1"></i>
                    {showChat
                      ? "Close Chat"
                      : messages.length > 0
                        ? "View Chat"
                        : "Start Chat"}
                    {unreadCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>

        {(() => {
          const appData = applicationData as any;
          const profileUser = employeesState?.data?.data || employeesState?.data;
          const profileDetails = profileUser?.details || profileUser || {};
          const storedUser = (() => {
            try {
              const u = localStorage.getItem("user");
              return u && u !== "undefined" ? JSON.parse(u) : {};
            } catch {
              return {};
            }
          })();
          const storedDetails = storedUser?.details || storedUser || {};

          const empDetails =
            appData?.employee?.details ||
            appData?.employee ||
            appData?.employeeDetails?.details ||
            appData?.employeeDetails ||
            {};

          const sapInfo =
            empDetails?.sap_department_designation_wings ||
            profileDetails?.sap_department_designation_wings ||
            storedDetails?.sap_department_designation_wings ||
            {};

          const formatCategory = (cat: any) => {
            if (!cat) return "-";
            const rawStr =
              typeof cat === "object" && cat !== null
                ? (cat.category_title || cat.category_name || cat.title || cat.name || cat.category_id || "")
                : String(cat);
            const str = rawStr.trim().toUpperCase();
            if (str === "G" || str === "GEN" || str === "GENERAL") return "GENERAL";
            if (str === "O" || str === "OBC") return "OBC";
            if (str === "SC") return "SC";
            if (str === "ST") return "ST";
            if (str === "EWS") return "EWS";
            return rawStr || "-";
          };

          const empName =
            (empDetails.firstname || profileDetails.firstname || storedDetails.firstname)
              ? `${empDetails.firstname || profileDetails.firstname || storedDetails.firstname} ${empDetails.lsastname || empDetails.lastname || profileDetails.lsastname || profileDetails.lastname || storedDetails.lsastname || storedDetails.lastname || ""}`.trim()
              : empDetails.fullName || profileDetails.fullName || storedDetails.fullName || appData.employeeName || "-";

          const designation =
            sapInfo.designation_name ||
            empDetails.designation ||
            profileDetails.designation ||
            storedDetails.designation ||
            empDetails.designation_details?.designationName ||
            profileDetails.designation_details?.designationName ||
            storedDetails.designation_details?.designationName ||
            appData.designation ||
            "-";

          const dateOfJoining =
            empDetails.date_of_joining ||
            profileDetails.date_of_joining ||
            storedDetails.date_of_joining ||
            empDetails.doj ||
            profileDetails.doj ||
            storedDetails.doj ||
            appData.dateOfJoining ||
            appData.doj;

          const employmentStatus =
            empDetails.employmentType ||
            profileDetails.employmentType ||
            storedDetails.employmentType ||
            empDetails.employment_status ||
            profileDetails.employment_status ||
            storedDetails.employment_status ||
            empDetails.employeeStatus ||
            profileDetails.employeeStatus ||
            storedDetails.employeeStatus ||
            appData.employeeStatus ||
            appData.employmentType ||
            "Permanent";

          const department =
            appData.departmentName ||
            sapInfo.department_name ||
            empDetails.departmentName ||
            empDetails.department ||
            profileDetails.departmentName ||
            profileDetails.department ||
            storedDetails.departmentName ||
            storedDetails.department ||
            "-";

          const contactNumber =
            appData.contact_info ||
            appData.contactNumber ||
            empDetails.phone ||
            empDetails.contact_info ||
            profileDetails.phone ||
            profileDetails.contact_info ||
            storedDetails.phone ||
            storedDetails.contact_info ||
            "-";

          const emailId =
            appData.emailId ||
            empDetails.email ||
            profileDetails.email ||
            storedDetails.email ||
            "-";

          const dateOfBirth =
            appData.dateOfBirth ||
            empDetails.birthdate ||
            profileDetails.birthdate ||
            storedDetails.birthdate ||
            empDetails.dob ||
            profileDetails.dob;

          const category = formatCategory(
            appData.employeeDetails?.category ||
            empDetails.category ||
            profileDetails.category ||
            storedDetails.category ||
            appData.category
          );

          const lwp =
            empDetails.lwp ||
            profileDetails.lwp ||
            storedDetails.lwp ||
            appData.lwp ||
            "00000";

          const calculateAge = (dateStr: string | undefined): string => {
            if (!dateStr || dateStr === "-" || dateStr === "N/A") return "";
            let birthDate;
            if (dateStr.includes("-") && dateStr.split("-").length === 3) {
              const parts = dateStr.split("-");
              if (parts[0].length === 2) {
                birthDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              } else {
                birthDate = new Date(dateStr);
              }
            } else {
              birthDate = new Date(dateStr);
            }
            if (isNaN(birthDate.getTime())) return "";
            const today = new Date();
            let years = today.getFullYear() - birthDate.getFullYear();
            let months = today.getMonth() - birthDate.getMonth();
            let days = today.getDate() - birthDate.getDate();
            if (days < 0) {
              months--;
              const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
              days += lastMonth.getDate();
            }
            if (months < 0) {
              years--;
              months += 12;
            }
            return `${years}y, ${months}m, ${days}d`;
          };

          const age =
            appData.age ||
            empDetails.age ||
            profileDetails.age ||
            storedDetails.age ||
            calculateAge(dateOfBirth) ||
            "-";

          return (
            <>
              {/* 1. Application Details */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-briefcase me-2 text-primary"></i>
                    Application Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Ref No.</small>
                        <div className="fw-bold text-primary fs-15">{appData.referenceNumber || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                        <div className="fw-bold text-dark fs-15">{formatEmpIdForDisplay(appData.empID || appData.empNo) || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Currently With</small>
                        <div className="fw-bold text-dark fs-15">{formatEmpIdForDisplay(appData.approverId) || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Creation Date</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(appData.requestCreationDat || appData.created_at)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Organisation</small>
                        <div className="fw-bold text-dark fs-15">{appData.organisationInWhich || appData.organisation || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">D&AR Status</small>
                        <div className="fw-bold text-dark fs-15">{getDarStatusLabel(appData.dnARStatus || appData.dandARStatus)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Remarks by Controlling</small>
                        <div className="fw-bold text-dark fs-15">{appData.Remarks && appData.Remarks !== "X" ? appData.Remarks : (appData.remarks && appData.remarks !== "X" ? appData.remarks : "-")}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Final D&AR / Vigilance Clearance</small>
                        <div className="fw-bold text-dark fs-15">{appData.finalDandARVigilance || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Reason for Rejection (in case of rejection)</small>
                        <div className="fw-bold text-dark fs-15">{getRejectionReasonDisplay(appData.ReasonForRejection || appData.reasonForRejection || appData.selectedRejectionReason)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Employee Details */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-user me-2 text-primary"></i>
                    Employee Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Name</small>
                        <div className="fw-bold text-dark fs-15">{empName}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                        <div className="fw-bold text-dark fs-15">{designation}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining in DMRC</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(dateOfJoining)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Employment Status</small>
                        <div className="fw-bold text-dark fs-15">{employmentStatus}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                        <div className="fw-bold text-dark fs-15">{department}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Contact Number</small>
                        <div className="fw-bold text-dark fs-15">{contactNumber}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Email ID</small>
                        <div className="fw-bold text-dark fs-15">{emailId}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Date of Birth</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(dateOfBirth)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Category</small>
                        <div className="fw-bold text-dark fs-15">{category}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">LWP</small>
                        <div className="fw-bold text-dark fs-15">{lwp}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Age</small>
                        <div className="fw-bold text-dark fs-15">{age}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Declaration */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-shield-check me-2 text-primary"></i>
                    Declaration
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Whether any penalty has been imposed in last 10 years by D&AR</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.dandARPendingOrNot || appData.dandARStatus;
                            return val === "Y" || val === "Yes" ? "Yes" : val === "N" || val === "No" ? "No" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Whether any penalty has been imposed in last 10 years by Vigilance</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.vigilancePending;
                            return val === "Y" || val === "Yes" ? "Yes" : val === "N" || val === "No" ? "No" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Whether probation is confirmed in DMRC</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.whetherProbation;
                            return val === "N" || val === "No" ? "No" : val === "Y" || val === "Yes" ? "Yes" : val === "0" || val === "Not Applicable" ? "Not Applicable" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">
                          {appData.whetherProbation === "0" || appData.whetherProbation === "Not Applicable"
                            ? "Whether two years service completed after Absorption"
                            : "Whether after probation confirmation, one year regular service is complete"}
                        </span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.whetherProbationConfirmed || appData.threeyearsRegularServ;
                            return val === "N" || val === "No" ? "No" : val === "Y" || val === "Yes" ? "Yes" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Has the employee been imparted training abroad by DMRC</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.trainedAbroadByDM;
                            return val === "N" || val === "No" ? "No" : val === "Y" || val === "Yes" ? "Yes" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    {(appData.trainedAbroadByDM === "Y" || appData.trainedAbroadByDM === "Yes") && (
                      <>
                        <div className="col-md-6">
                          <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                            <span className="text-muted fw-medium">Place of Training Abroad</span>
                            <span className="fw-bold text-dark fs-15">
                              {appData.placeAbroadTrain || "-"}
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                            <span className="text-muted fw-medium">Year of Training Abroad</span>
                            <span className="fw-bold text-dark fs-15">
                              {appData.yearOfAbroadTrain || "-"}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. Advertisement Details */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-file-description me-2 text-primary"></i>
                    Advertisement Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">No. of Application Forwarded in the Year</small>
                        <div className="fw-bold text-dark fs-15">
                          {getOrdinalNumber(appData.appNoInYear || appData.noOfApplicaForwYears || "1")}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Place of Posting</small>
                        <div className="fw-bold text-dark fs-15">{appData.placeofPosting || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Nature of Outside Job</small>
                        <div className="fw-bold text-dark fs-15">
                          {appData.natureDetails?.natureTitle || appData.natureOfOutsideJob || appData.natureOfOutside || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Name of Post Applied For</small>
                        <div className="fw-bold text-primary fs-15">
                          {appData.postAppliedFor || appData.postAppliedfor || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Newspaper / Website</small>
                        <div className="fw-bold text-dark fs-15">{appData.newspaperWebsite || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Advertisement No.</small>
                        <div className="fw-bold text-dark fs-15">{appData.advertisementNo || appData.advertisementNo1 || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Advertisement Date</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(appData.advertisementDate || appData.advertisementDate1)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Name of the Organization in which Applied</small>
                        <div className="fw-bold text-dark fs-15">{appData.organisation || appData.organisationInWhich || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Last Date of Receipt of Application</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(appData.lastDateOfApplica || appData.lastDateReceipt)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Education Details */}
              {appData.education && appData.education.length > 0 && (
                <div className="card mt-4">
                  <div className="card-header bg-light">
                    <h5 className="card-title mb-0 text-dark fw-bold">
                      <i className="ti ti-school me-2 text-primary"></i>
                      Education Details ({appData.education.length})
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="d-flex flex-column gap-3">
                      {appData.education.map((edu: any, index: number) => (
                        <div key={index} className="p-3 bg-light rounded border">
                          <h6 className="text-primary fw-bold mb-3">
                            Education Details {index + 1}
                          </h6>
                          <div className="row g-3">
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Course Name</small>
                              <div className="fw-bold text-dark">{edu.courseName || "-"}</div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Date of Admission</small>
                              <div className="fw-bold text-dark">{formatDate(edu.courseDateOfAdmission)}</div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Date of Passing</small>
                              <div className="fw-bold text-dark">{formatDate(edu.courseDateOfPassing)}</div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">When Qualification Acquired</small>
                              <div className="fw-bold text-dark">
                                {edu.qualificationAcquir === "A" ? "After Joining" : edu.qualificationAcquir === "B" ? "Before Joining" : "-"}
                              </div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Course Affiliated by UGC/AICTE</small>
                              <div className="fw-bold text-dark">{edu.isCourseRecog === "Y" ? "Yes" : "No"}</div>
                            </div>
                          </div>

                          {/* Attached edu_documents matching mobile OutJobDisplay.tsx */}
                          {(() => {
                            const rawDocs = [
                              ...(Array.isArray(edu?.edu_documents) ? edu.edu_documents : []),
                              ...(Array.isArray(appData?.documents)
                                ? appData.documents.filter(
                                  (doc: any) =>
                                    (edu.id && doc.request_education_id === edu.id) ||
                                    (Array.isArray(appData.education) &&
                                      appData.education.length === 1 &&
                                      (doc.typeOfAttachedDoc === "N" ||
                                        doc.typeOfAttachedDoc === "E" ||
                                        doc.typeOfAttachedDoc === "noc" ||
                                        doc.typeOfAttachedDoc === "education" ||
                                        doc.typeOfAttachedDoc === "qualification" ||
                                        doc.typeOfAttachedDoc === "NOC/Addition"))
                                )
                                : []),
                            ];

                            // Deduplicate by fileName & length < 20 (matching mobile OutJobDisplay.tsx)
                            const uniqueDocs = rawDocs
                              .filter((d: any) => !d.referenceNumber || d.referenceNumber.toString().length < 20)
                              .filter((d: any, i: number, self: any[]) =>
                                i === self.findIndex((t: any) => t.fileName === d.fileName)
                              );

                            if (uniqueDocs.length === 0) return null;

                            // Only display Education/Qualification Certificates here (NOC or Addition Office Order is shown in Application Attachments)
                            const eduCertDocs = uniqueDocs.filter((doc: any) => {
                              const typeStr = (doc.typeOfAttachedDoc || doc.documentType || "").toString().toUpperCase();
                              const isNoc =
                                typeStr === "N" ||
                                typeStr.includes("NOC") ||
                                typeStr.includes("ADDITION") ||
                                (doc.fileName && doc.fileName.toLowerCase().includes("noc"));
                              return !isNoc;
                            });

                            if (eduCertDocs.length === 0) return null;

                            return (
                              <div className="mt-3 pt-3 border-top">
                                <div className="mb-3">
                                  <small className="text-muted d-block uppercase fw-medium mb-2">
                                    <i className="ti ti-school text-primary me-1"></i>
                                    Education/Qualification Certificates
                                  </small>
                                  <div className="d-flex flex-wrap gap-2">
                                    {eduCertDocs.map((doc: any, dIdx: number) => (
                                      <button
                                        key={doc.id || dIdx}
                                        type="button"
                                        className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                                        onClick={() => handleDocumentClick(doc)}
                                        title="View Document"
                                        disabled={!doc.fileName && !doc.UUID && !doc.docID}
                                      >
                                        <i className="ti ti-file-text"></i>
                                        <span>{doc.fileName ? doc.fileName.split("/").pop() : `Document ${dIdx + 1}`}</span>
                                        <i className="ti ti-eye ms-1"></i>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 6. Attachments Section (includes Advertisement and NOC/Addition Office Order) */}
              {(() => {
                const mainDocs = Array.isArray(appData.documents) ? appData.documents : [];
                const eduDocs = Array.isArray(appData.education)
                  ? appData.education.flatMap((e: any) => (Array.isArray(e.edu_documents) ? e.edu_documents : []))
                  : [];
                const allDocs = [...mainDocs, ...eduDocs];
                const allNocDocuments = allDocs
                  .filter((doc: any) => {
                    const typeStr = (doc.typeOfAttachedDoc || doc.documentType || "").toString().toUpperCase();
                    const isNoc = typeStr === "N" || typeStr.includes("NOC") || typeStr.includes("ADDITION");
                    const isEduCert = !isNoc && (typeStr === "E" || typeStr === "QUALIFICATION" || typeStr === "EDUCATION" || typeStr.includes("QUAL"));
                    // Exclude only qualification certificates (they remain in Educational Details section)
                    if (isEduCert) return false;
                    return true;
                  })
                  .filter(
                    (doc: any, idx: number, self: any[]) =>
                      idx === self.findIndex((t: any) => (t.id && doc.id ? t.id === doc.id : t.fileName === doc.fileName))
                  )
                  .filter(
                    (doc: any) => (!doc.referenceNumber ? true : doc.referenceNumber.toString().length < 20)
                  );

                if (!allNocDocuments || allNocDocuments.length === 0) return null;

                return (
                  <div className="card mt-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0 text-dark fw-bold">
                        <i className="ti ti-files me-2 text-primary"></i>
                        Attachments ({allNocDocuments.length})
                      </h5>
                    </div>
                    <div className="card-body">
                      {/* Mandatory Attachments Info Card */}
                      <div className="alert alert-danger bg-danger-transparent border-danger mb-3 p-3">
                        <h6 className="text-danger mb-2">
                          <i className="ti ti-info-circle me-2"></i>
                          Mandatory Attachments
                        </h6>
                        <ul className="mb-0 ps-3 small text-muted">
                          <li>- Copy of advertisement indicating last date of application and post applied for must be attached.</li>
                          <li>- If, with the existing educational qualifications (as captured in SAP), the employee is not eligible for the post, uploading requisite educational qualification documents is mandatory, on basis of which the employee is eligible for the post that he/she intends to apply for.</li>
                          <li>- Documents to be uploaded - Final Marksheet/Certificate</li>
                        </ul>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">File Name</th>
                              <th scope="col">Document Type</th>
                              <th scope="col">Upload Date</th>
                              <th scope="col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allNocDocuments.map((doc: any, index: number) => {
                              const typeStr = (doc.typeOfAttachedDoc || doc.documentType || "").toString().toUpperCase();
                              const isNoc = typeStr === "N" || typeStr.includes("NOC") || typeStr.includes("ADDITION");
                              const isEdu = typeStr === "E" || typeStr.includes("QUAL") || typeStr.includes("EDU");
                              const isAdv = typeStr === "A" || typeStr.includes("ADV");
                              const badgeClass = isNoc ? "bg-info" : isEdu ? "bg-success" : isAdv ? "bg-dark" : "bg-secondary";
                              const badgeLabel = isNoc
                                ? "NOC or Addition Office Order"
                                : isEdu
                                  ? "Education/Qualification Certificates"
                                  : isAdv
                                    ? "Advertisement for Outside Job"
                                    : doc.typeOfAttachedDoc || "General";

                              return (
                                <tr key={index}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <i className="ti ti-file-text text-primary me-2"></i>
                                      {index + 1}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fw-medium">
                                      {doc.fileName ? doc.fileName.split("/").pop() : `File ${index + 1}`}
                                    </div>
                                  </td>
                                  <td>
                                    <span className={`badge ${badgeClass}`}>
                                      {badgeLabel}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="text-muted">
                                      {formatDate((doc as any).created_at)}
                                    </span>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleDocumentClick(doc)}
                                      title="View/Download File"
                                      disabled={!doc.fileName && !doc.UUID && !doc.docID}
                                    >
                                      <i className="ti ti-eye me-1"></i>
                                      View
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 7. Previous Remarks & Approval History Section */}
              {(() => {
                let rawList: any[] = [
                  ...(Array.isArray(appData?.remarks_data) && appData.remarks_data.length > 0
                    ? appData.remarks_data
                    : Array.isArray(appData?.history) && appData.history.length > 0
                      ? appData.history
                      : Array.isArray(appData?.work_history) && appData.work_history.length > 0
                        ? appData.work_history
                        : Array.isArray(appData?.remarksHistory) && appData.remarksHistory.length > 0
                          ? appData.remarksHistory
                          : [])
                ].map((item: any) => ({ ...item }));

                const applicantId = String(
                  appData?.empId ||
                  appData?.empID ||
                  appData?.employee?.empId ||
                  appData?.employee?.empID ||
                  ""
                ).replace(/^0+/, "");

                const hasEmployeeEntry = rawList.some((item: any) => {
                  const itemUserId = String(
                    item?.approverUserID || item?.userId || item?.empID || ""
                  ).replace(/^0+/, "");
                  return (
                    item?.usertype === "E" ||
                    item?.usertype === "Employee" ||
                    item?.approverName === "User" ||
                    (applicantId && itemUserId && itemUserId === applicantId)
                  );
                });

                const employeeRemarks = (() => {
                  const candidates = [
                    appData?.anyRemarks,
                    appData?.any_remarks,
                    appData?.employeeRemarks,
                    appData?.empRemarks,
                    appData?.employee_remarks,
                    appData?.applicantRemarks,
                    appData?.userRemarks,
                  ];
                  for (const cand of candidates) {
                    if (typeof cand === "string" && cand.trim() && cand.trim() !== "X" && cand.trim() !== "-" && cand.trim() !== "null") {
                      return cand.trim();
                    }
                  }
                  return "";
                })();

                const employeeTimestamp =
                  appData?.requestCreationDate ||
                  appData?.created_at ||
                  appData?.createdAt ||
                  appData?.sap_created_at ||
                  appData?.sapCreatedAt ||
                  appData?.appliedDate ||
                  null;

                const employeeName = (() => {
                  if (appData?.employee) {
                    const fname = appData.employee.firstname || appData.employee.firstName || "";
                    const lname = appData.employee.lsastname || appData.employee.lastname || appData.employee.lastName || "";
                    const full = `${fname} ${lname}`.trim();
                    if (full) return full;
                  }
                  return appData?.employeeName || appData?.empName || "Employee";
                })();

                const employeeUserId =
                  appData?.empId || appData?.empID || appData?.employee?.empId || appData?.employee?.empID || "";

                if (hasEmployeeEntry) {
                  rawList = rawList.map((item: any) => {
                    const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                    const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                    if (isEmp && !item?.remarks && employeeRemarks) {
                      return { ...item, remarks: employeeRemarks };
                    }
                    return { ...item };
                  });
                } else if (appData && (employeeUserId || employeeName !== "Employee" || appData?.id || appData?.referenceNumber)) {
                  rawList.push({
                    usertype: "Employee",
                    approverUserID: employeeUserId,
                    userId: employeeUserId,
                    empID: employeeUserId,
                    approverName: employeeName,
                    name: employeeName,
                    remarks: employeeRemarks,
                    createdAt: employeeTimestamp,
                    isEmployee: true,
                  });
                }

                if (rawList.length === 0) return null;

                const sortedRemarks = [...rawList].sort((a: any, b: any) => {
                  const dateA = new Date(a.createdAt || a.created_at || a.timeStamp || 0).getTime();
                  const dateB = new Date(b.createdAt || b.created_at || b.timeStamp || 0).getTime();
                  return (isNaN(dateB) ? 0 : dateB) - (isNaN(dateA) ? 0 : dateA);
                });

                return (
                  <div className="card mt-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0 text-dark fw-bold">
                        <i className="ti ti-history me-2 text-primary"></i>
                        Remarks & Approval History
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="d-flex flex-column gap-3">
                        {sortedRemarks.map((item: any, index: number) => {
                          const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                          const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                          return (
                            <div
                              key={index}
                              className={`p-3 bg-white rounded border border-start-4 ${isEmp ? "border-start-info" : "border-start-primary"} shadow-xs`}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <div className={`avatar avatar-sm ${isEmp ? "bg-info-transparent text-info" : "bg-primary-transparent text-primary"} rounded-circle fw-bold`}>
                                    <i className={`${isEmp ? "ti ti-user" : "ti ti-user-check"} fs-16`}></i>
                                  </div>
                                  <div>
                                    <div className="d-flex align-items-center gap-2">
                                      <h6 className="mb-0 text-dark fw-bold">
                                        {item.approverName ||
                                          item.name ||
                                          item.history_approver?.name ||
                                          (isEmp ? "Employee" : "Approver")}
                                      </h6>
                                      <span className={`badge ${isEmp ? "bg-info-subtle text-info" : "bg-warning-subtle text-warning"} border text-xs`}>
                                        {isEmp ? "Employee" : "Approver"}
                                      </span>
                                    </div>
                                    <small className="text-muted">
                                      ID:{" "}
                                      {formatEmpIdForDisplay(
                                        item.approverUserID ||
                                        item.userId ||
                                        item.history_approver?.empId ||
                                        (isEmp ? applicantId : "")
                                      ) || "-"}
                                    </small>
                                  </div>
                                </div>
                                <span className="badge bg-light text-muted border">
                                  <i className="ti ti-calendar me-1"></i>
                                  {formatDate(
                                    item.createdAt ||
                                    item.created_at ||
                                    item.timeStamp
                                  )}
                                  {(item.createdAt || item.created_at || item.timeStamp) && !isNaN(new Date(item.createdAt || item.created_at || item.timeStamp).getTime())
                                    ? " " + new Date(item.createdAt || item.created_at || item.timeStamp).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                      hour12: true,
                                    }).toUpperCase()
                                    : ""}
                                </span>
                              </div>
                              <div className="mt-2 pt-2 border-top">
                                <p className="mb-0 text-dark fw-medium fs-14">
                                  {item.remarks && String(item.remarks).trim() !== "-" && String(item.remarks).trim() !== "X" ? (
                                    <span>
                                      <i className="ti ti-quote text-muted me-1"></i>
                                      {item.remarks}
                                    </span>
                                  ) : isEmp ? (
                                    <span>&nbsp;</span>
                                  ) : (
                                    <span className="text-muted fst-italic">No remarks</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 8. Declaration Section Footer */}
              <div className="card mt-4 bg-light border-0 shadow-xs">
                <div className="card-body">
                  <h6 className="fw-bold text-dark mb-2">
                    <i className="ti ti-file-certificate me-2 text-primary"></i>
                    Declaration
                  </h6>
                  <ul className="mb-0 ps-3 small text-muted">
                    <li>I hereby declare that the information given above is true.</li>
                    <li>I declare that I am prima facie meeting the eligibility criteria (educational qualification, age, experience, pay scale, etc.) for the post I intend to apply for, as per the enclosed/uploaded advertisement.</li>
                    {(appData.natureOfOutsideJob === "Deputation" || appData.natureDetails?.natureTitle === "Deputation") && (
                      <li>I am aware about the terms and conditions of Deputation, as applicable to the employees of DMRC and I shall provide a copy of the same to the outside organization in which I intend to apply for employment, on deputation basis.</li>
                    )}
                  </ul>
                </div>
              </div>

              {/* 9. Read Only Notice */}
              <div className="alert alert-success bg-success-transparent border-success d-flex align-items-center p-3 mt-4 mb-0">
                <i className="ti ti-info-circle fs-20 me-2 text-success"></i>
                <span className="text-dark fs-14">This is a submitted application. You can view details and track approval status.</span>
              </div>
            </>
          );
        })()}

        {/* Enhanced Chat Drawer - Corner Popup Style */}
        {showChat && (
          <>
            {/* Soft click-away backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100"
              style={{
                zIndex: 99990,
                backgroundColor: "rgba(15, 23, 42, 0.35)",
                backdropFilter: "blur(2px)",
                transition: "opacity 0.2s ease",
              }}
              onClick={handleChatToggle}
            />

            {/* Full Screen Right Side Panel */}
            <div
              className="position-fixed bg-white chat-corner-widget d-flex flex-column"
              style={{
                zIndex: 99999,
                top: 0,
                bottom: 0,
                right: 0,
                height: "100vh",
                width: isFullScreen ? "100vw" : "480px",
                maxWidth: "100vw",
                borderRadius: 0,
                borderLeft: "1px solid #e2e8f0",
                boxShadow: "-4px 0 24px rgba(15, 23, 42, 0.08)",
                transition: "width 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
            >
              {/* Chat Header */}
              <div className="d-flex align-items-center justify-content-between px-3.5 py-3 border-bottom bg-white flex-shrink-0 chat-widget-header">
                <div className="d-flex align-items-center gap-2.5">
                  <div className="position-relative flex-shrink-0">
                    <div
                      className="avatar rounded-2 d-flex align-items-center justify-content-center fw-bold shadow-xs"
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "6px",
                        background: "linear-gradient(135deg, #f26522 0%, #d94e0c 100%)",
                        color: "#fff",
                      }}
                    >
                      <i className="ti ti-messages fs-18"></i>
                    </div>
                    <span
                      className="position-absolute bottom-0 end-0 bg-success border border-2 border-white"
                      style={{ width: "9px", height: "9px", borderRadius: "2px" }}
                      title="Active"
                    />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0 fw-bold text-dark fs-14">Chat - Outside Job</h6>
                      <span className="badge bg-success-subtle text-success fs-10 px-2 py-0.5 rounded-1 fw-semibold">
                        Active
                      </span>
                    </div>
                    <small className="text-muted fs-11 d-block text-truncate" style={{ maxWidth: "230px" }}>
                      Ref: #{applicationData.referenceNumber || applicationData.id}
                      {applicationData.empID ? ` • Emp: ${applicationData.empID}` : ""}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-1.5">
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={() => setIsFullScreen(!isFullScreen)}
                    title={isFullScreen ? "Restore to Side Panel" : "Expand to Full Screen"}
                  >
                    <i className={`ti ${isFullScreen ? "ti-arrows-minimize" : "ti-arrows-maximize"} fs-14`}></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={() =>
                      dispatch(
                        fetchMessages({ nocRequestId: applicationData.id })
                      )
                    }
                    disabled={chatLoading}
                    title="Refresh Messages"
                  >
                    <i className={`ti ti-refresh fs-14 ${chatLoading ? "ti-spin text-primary" : ""}`}></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={handleChatToggle}
                    title="Close Chat"
                  >
                    <i className="ti ti-x fs-16"></i>
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="chat-messages-container p-3 flex-grow-1 overflow-auto" style={{ backgroundColor: "#f8fafc" }}>
                {(chatError || uploadError) && (
                  <div className="alert alert-danger alert-dismissible py-2 px-3 fs-12 mb-3 rounded-2 shadow-xs">
                    <button
                      type="button"
                      className="btn-close fs-10"
                      onClick={() => {
                        dispatch(clearError());
                        dispatch(clearUploadError());
                      }}
                    ></button>
                    {chatError || uploadError}
                  </div>
                )}

                {chatLoading && messages.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted small mt-2 fs-12">Loading conversation...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center py-5 my-auto text-center"
                    style={{ minHeight: "280px" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: "72px",
                        height: "72px",
                        borderRadius: "50%",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #f1f5f9",
                        color: "#94a3b8",
                      }}
                    >
                      <i className="ti ti-messages fs-36" style={{ opacity: 0.75 }}></i>
                    </div>
                    <h6 className="fw-semibold mb-1" style={{ color: "#64748b", fontSize: "15px" }}>
                      No messages yet
                    </h6>
                    <p className="small mb-0" style={{ color: "#94a3b8", fontSize: "12.5px" }}>
                      Start the conversation below
                    </p>
                  </div>
                ) : (
                  <>
                    {(() => {
                      // Group messages by date - matching mobile StartChat.tsx getGroupedMessages()
                      const grouped: any[] = [];
                      let lastDate = "";
                      messages.forEach((msg: ChatMessage, index: number) => {
                        const msgDate = msg.createdAt ? new Date(msg.createdAt) : new Date();
                        const dateStr = msgDate.toDateString();
                        const today = new Date().toDateString();
                        const yesterday = new Date(Date.now() - 86400000).toDateString();
                        let displayDate = dateStr;
                        if (dateStr === today) displayDate = "Today";
                        else if (dateStr === yesterday) displayDate = "Yesterday";
                        else displayDate = msgDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
                        if (displayDate !== lastDate) {
                          grouped.push({ type: "date", date: displayDate, id: `date-${index}` });
                          lastDate = displayDate;
                        }
                        grouped.push({ ...msg, type: "message", _idx: index });
                      });
                      return grouped;
                    })().map((item: any) => {
                      if (item.type === "date") {
                        return (
                          <div key={item.id} className="d-flex align-items-center my-3">
                            <div style={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
                            <span
                              className="px-3 fw-semibold"
                              style={{
                                fontSize: "11px",
                                color: "#64748b",
                                letterSpacing: "0.2px",
                              }}
                            >
                              {item.date}
                            </span>
                            <div style={{ flex: 1, height: "1px", backgroundColor: "#e2e8f0" }} />
                          </div>
                        );
                      }
                      const index: number = item._idx;
                      const msg: ChatMessage = item;
                      return ((() => {
                      const isOwn = isMyMessage(msg);
                      const rawMsg = msg.message || "";
                      const paperclipIdx = rawMsg.indexOf("📎");
                      const hasPaperclip = paperclipIdx !== -1;
                      const displayMsgText = hasPaperclip
                        ? rawMsg.substring(0, paperclipIdx).trim()
                        : rawMsg.trim();
                      let extractedAttNames: string[] = [];
                      if (hasPaperclip) {
                        const attPart = rawMsg.substring(paperclipIdx).replace(/^[📎\s]+/, "").trim();
                        extractedAttNames = attPart.split(",").map((s: string) => s.trim()).filter(Boolean);
                      }
                      const attachmentsList: any[] =
                        msg.documents && msg.documents.length > 0
                          ? msg.documents
                          : msg.attachments && msg.attachments.length > 0
                            ? msg.attachments
                            : extractedAttNames.length > 0
                              ? extractedAttNames
                              : [];

                      return (
                        <div
                          key={msg.id || index}
                          className={`d-flex mb-3.5 ${isOwn ? "justify-content-end" : "justify-content-start"}`}
                        >
                          {/* Approver/Participant Avatar */}
                          {!isOwn && (
                            <div className="me-2 flex-shrink-0 align-self-end mb-0.5">
                              <EmployeeAvatar
                                empName={msg.senderName || "Approver"}
                                empImage={msg.senderImage}
                                empId={msg.senderEmpId}
                                size="sm"
                                shape="rounded"
                                showName={false}
                                showEmpId={false}
                                className="rounded-2 shadow-xs"
                              />
                            </div>
                          )}

                          <div
                            className="position-relative"
                            style={{ maxWidth: "84%", wordBreak: "break-word" }}
                          >
                            {/* Sender Label Above Bubble */}
                            <div
                              className={`d-flex align-items-center gap-1.5 mb-1 px-1 ${isOwn ? "justify-content-end" : "justify-content-start"
                                }`}
                            >
                              <span className="fw-semibold text-dark fs-11">
                                {isOwn ? "You" : (msg.senderName || "Approver")}
                              </span>
                              <span
                                className={`badge fs-9 px-1.5 py-0.5 text-uppercase rounded-1 fw-semibold ${isOwn
                                  ? "bg-primary-subtle text-primary"
                                  : msg.senderType === "approver"
                                    ? "bg-warning-subtle text-warning-emphasis"
                                    : "bg-secondary-subtle text-secondary"
                                  }`}
                              >
                                {msg.senderType || (isOwn ? "employee" : "approver")}
                              </span>
                            </div>

                            <div
                              className="p-2.5 px-3 bg-white border shadow-xs position-relative"
                              style={{
                                minWidth: "120px",
                                borderRadius: isOwn ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                                borderColor: "#e2e8f0",
                              }}
                            >
                              {msg.documentType && (
                                <div className="mb-2">
                                  <span
                                    className="badge fs-10 fw-medium px-2 py-0.5 rounded-1 bg-warning-subtle text-warning-emphasis border border-warning"
                                  >
                                    <i className="ti ti-file-certificate me-1"></i>
                                    Requested: {msg.documentType}
                                  </span>
                                </div>
                              )}

                              {displayMsgText && (
                                <p
                                  className="mb-0 chat-message-content text-dark"
                                  style={{
                                    fontSize: "13.5px",
                                    lineHeight: "1.45",
                                    color: "#1e293b",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {displayMsgText}
                                </p>
                              )}

                              {/* Render Interactive Attachments / Images */}
                              {attachmentsList.length > 0 && (
                                <div
                                  className={`d-flex flex-column gap-2 ${displayMsgText ? "mt-2.5 pt-2 border-top" : ""
                                    }`}
                                  style={{
                                    borderColor: isOwn ? "rgba(255, 255, 255, 0.25)" : "#f1f5f9",
                                  }}
                                >
                                  {attachmentsList.map((attachment: any, attIdx: number) => {
                                    const rawName =
                                      typeof attachment === "string"
                                        ? attachment
                                        : attachment.fileName ||
                                        attachment.name ||
                                        attachment.FileName ||
                                        "Attachment";
                                    const fileName =
                                      rawName.split("/").pop()?.split("-").slice(2).join("-") ||
                                      rawName.split("/").pop() ||
                                      "Attachment";
                                    const directUrl =
                                      typeof attachment === "object"
                                        ? attachment.url
                                        : rawName.startsWith("http")
                                          ? rawName
                                          : undefined;
                                    const isImg =
                                      /\.(jpg|jpeg|png|webp|gif)$/i.test(fileName) ||
                                      (typeof attachment === "object" &&
                                        attachment.contentType?.startsWith("image/"));
                                    const isPdf =
                                      /\.pdf$/i.test(fileName) ||
                                      (typeof attachment === "object" &&
                                        attachment.contentType === "application/pdf");
                                    const fileSize =
                                      typeof attachment === "object" ? attachment.fileSize : undefined;

                                    return (
                                      <div key={attIdx} className="d-flex flex-column gap-1">
                                        {/* Inline image preview if direct url exists */}
                                        {isImg && directUrl && (
                                          <div
                                            className="overflow-hidden rounded-2 border mb-1 cursor-pointer position-relative shadow-xs"
                                            style={{
                                              maxHeight: "190px",
                                              maxWidth: "280px",
                                              borderRadius: "6px",
                                              backgroundColor: "#000",
                                            }}
                                            onClick={() => handleAttachmentClick(attachment)}
                                            title="Click to view full image"
                                          >
                                            <img
                                              src={directUrl}
                                              alt={fileName}
                                              className="w-100 h-100 object-fit-cover"
                                              style={{
                                                maxHeight: "190px",
                                                display: "block",
                                                cursor: "pointer",
                                              }}
                                            />
                                          </div>
                                        )}
                                        {/* Mobile-styled Attachment Card (Screenshot 4) */}
                                        <div
                                          className="d-flex align-items-center justify-content-between p-2 mt-1.5 cursor-pointer"
                                          onClick={() => handleAttachmentClick(attachment)}
                                          style={{
                                            minWidth: "200px",
                                            maxWidth: "280px",
                                            borderRadius: "10px",
                                            border: "1px solid #e2e8f0",
                                            backgroundColor: "#f8fafc",
                                            transition: "background-color 0.15s ease",
                                          }}
                                          title="Tap to open attachment"
                                        >
                                          <div className="d-flex align-items-center gap-2.5 overflow-hidden me-2">
                                            <div
                                              className="d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                              style={{
                                                width: "36px",
                                                height: "36px",
                                                borderRadius: "8px",
                                                backgroundColor: isPdf ? "#FF5252" : isImg ? "#2196F3" : "#64748b",
                                              }}
                                            >
                                              <i className={`ti ${isPdf ? "ti-file-text" : isImg ? "ti-photo" : "ti-file"} fs-18`}></i>
                                            </div>
                                            <div className="overflow-hidden">
                                              <div
                                                className="text-truncate fw-semibold text-dark"
                                                style={{ fontSize: "12.5px", maxWidth: "160px", lineHeight: 1.25 }}
                                                title={fileName}
                                              >
                                                {fileName}
                                              </div>
                                              <small className="text-muted d-block" style={{ fontSize: "10.5px", marginTop: "2px" }}>
                                                {isPdf ? "PDF" : isImg ? "IMAGE" : "FILE"} • Tap to open
                                              </small>
                                            </div>
                                          </div>
                                          <div className="flex-shrink-0 text-muted pe-1">
                                            <i className="ti ti-chevron-right fs-15 text-muted"></i>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Timestamp */}
                              <div className="d-flex align-items-center justify-content-end gap-1.5 mt-1.5">
                                <small
                                  style={{
                                    fontSize: "10px",
                                    color: isOwn ? "rgba(255, 255, 255, 0.85)" : "#94a3b8",
                                  }}
                                >
                                  {formatMessageTime(msg.createdAt)}
                                </small>
                                {isOwn && <i className="ti ti-checks fs-11 text-white-50"></i>}
                              </div>
                            </div>
                          </div>

                          {/* Employee Avatar */}
                          {isOwn && (
                            <div className="ms-2 flex-shrink-0 align-self-end mb-0.5">
                              <EmployeeAvatar
                                empName={
                                  msg.senderName ||
                                  applicationData.employeeName ||
                                  "User"
                                }
                                empImage={msg.senderImage || (applicationData as any).employeeDetails?.image}
                                empId={msg.senderEmpId || applicationData.empID}
                                size="sm"
                                shape="rounded"
                                showName={false}
                                showEmpId={false}
                                className="rounded-2 shadow-xs"
                              />
                            </div>
                          )}
                        </div>
                      );
                      })());
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input Area (Mobile-identical Screenshots 2 & 3) */}
              <div className="chat-input-container bg-white border-top flex-shrink-0">
                {/* File Attachments Preview (Screenshot 2) */}
                {selectedFiles.length > 0 && (
                  <div className="px-3 pt-2 pb-1 bg-transparent">
                    <div className="d-flex flex-column gap-2">
                      {selectedFiles.map((file, index) => {
                        const fileSizeKB = Math.round(file.size / 1024);
                        const ext = (file.name.split(".").pop() || "").toLowerCase();
                        const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(file.name);
                        const isPdf = /\.pdf$/i.test(file.name);

                        return (
                          <div
                            key={index}
                            className="d-flex align-items-center justify-content-between p-2.5 bg-white border shadow-xs"
                            style={{
                              borderRadius: "14px",
                              borderColor: "#e2e8f0",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              const blobUrl = URL.createObjectURL(file);
                              window.open(blobUrl, "_blank");
                            }}
                            title="Tap to preview"
                          >
                            <div className="d-flex align-items-center gap-2.5 overflow-hidden me-2">
                              {/* Mobile Red/Blue rounded badge */}
                              <div
                                className="d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  borderRadius: "10px",
                                  backgroundColor: isPdf ? "#FF5252" : isImg ? "#2196F3" : "#64748b",
                                }}
                              >
                                <i className={`ti ${isPdf ? "ti-file-text" : isImg ? "ti-photo" : "ti-file"} fs-20`}></i>
                              </div>

                              <div className="overflow-hidden">
                                <div
                                  className="text-truncate fw-semibold text-dark"
                                  style={{ fontSize: "13.5px", maxWidth: "240px", lineHeight: 1.25 }}
                                  title={file.name}
                                >
                                  {file.name}
                                </div>
                                <small className="text-muted d-flex align-items-center gap-1.5 flex-wrap" style={{ fontSize: "11px", marginTop: "2px" }}>
                                  {selectedDocumentType && (
                                    <span
                                      className="badge px-1.5 py-0.5 fw-medium"
                                      style={{
                                        backgroundColor: "#fef2f2",
                                        color: "#b91c1c",
                                        border: "1px solid #fee2e2",
                                        fontSize: "9.5px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      <i className="ti ti-file-text me-1"></i>
                                      {selectedDocumentType}
                                    </span>
                                  )}
                                  <span>{ext.toUpperCase()} • {fileSizeKB}KB • Ready — Tap to preview</span>
                                </small>
                              </div>
                            </div>

                            {/* Delete Button */}
                            <button
                              type="button"
                              className="btn p-0 flex-shrink-0 d-flex align-items-center justify-content-center"
                              style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                backgroundColor: "#94a3b8",
                                color: "#ffffff",
                                border: "none",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFile(index);
                              }}
                              title="Remove file"
                            >
                              <i className="ti ti-x fs-13"></i>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Hidden File Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelection}
                  multiple
                  className="d-none"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                />

                {/* Active Document Type Tag */}
                {selectedDocumentType && (
                  <div className="px-3 pt-1 pb-1 flex-shrink-0">
                    <span
                      className="badge bg-white text-dark border d-inline-flex align-items-center gap-1.5 fs-11 fw-normal px-2.5 py-1"
                      style={{ borderRadius: "16px", borderColor: "#e2e8f0" }}
                    >
                      <i className="ti ti-file-text text-danger fs-12"></i>
                      <span>Document: <strong>{selectedDocumentType}</strong></span>
                      <button
                        type="button"
                        className="btn btn-link text-muted p-0 ms-1 d-flex align-items-center"
                        style={{ textDecoration: "none" }}
                        onClick={() => setSelectedDocumentType("")}
                        title="Clear document type tag"
                      >
                        <i className="ti ti-x fs-10"></i>
                      </button>
                    </span>
                  </div>
                )}

                {/* Input Error Message Banner (Direct Attachment Requires Message) */}
                {inputError && (
                  <div className="px-3 pt-2 pb-1 flex-shrink-0">
                    <div
                      className="d-flex align-items-center justify-content-between p-2 px-2.5 rounded-3 border"
                      style={{
                        backgroundColor: "#fef2f2",
                        borderColor: "#fecaca",
                        color: "#b91c1c",
                        fontSize: "12.5px",
                        fontWeight: 500,
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ti ti-alert-circle fs-16 flex-shrink-0" style={{ color: "#dc2626" }}></i>
                        <span>{inputError}</span>
                      </div>
                      <button
                        type="button"
                        className="btn p-0 border-0 bg-transparent text-danger d-flex align-items-center ms-2"
                        onClick={() => setInputError(null)}
                        title="Dismiss"
                      >
                        <i className="ti ti-x fs-12"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Mobile-identical Pill Input Bar */}
                <div className="p-2.5 pt-1 bg-white">
                  <div
                    className="d-flex align-items-center bg-white border px-3 py-1.5"
                    style={{
                      borderRadius: "28px",
                      borderColor: inputError ? "#f87171" : "#e2e8f0",
                      minHeight: "46px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                      transition: "border-color 0.2s ease",
                    }}
                  >
                    {/* Attachment Button */}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                      onClick={() => {
                        if (isCurrentUserApprover) {
                          setShowDocumentTypeDialog(true);
                        } else {
                          triggerFileInput();
                        }
                      }}
                      disabled={uploading || isSending}
                      style={{
                        textDecoration: "none",
                        width: "32px",
                        height: "32px",
                        color: "#64748b",
                      }}
                      title={isCurrentUserApprover ? "Attach file or request document" : "Attach file"}
                    >
                      {uploading ? (
                        <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14 }}>
                          <span className="visually-hidden">...</span>
                        </div>
                      ) : (
                        <i className="ti ti-paperclip fs-19" style={{ color: "#64748b" }}></i>
                      )}
                    </button>

                    {/* Text input */}
                    <input
                      ref={messageInputRef}
                      type="text"
                      className="form-control border-0 bg-transparent shadow-none p-0 flex-grow-1"
                      placeholder="Start Typing..."
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        if (inputError) setInputError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      disabled={isSending}
                      style={{
                        fontSize: "13.5px",
                        color: "#1e293b",
                      }}
                    />

                    {/* Circular Send Button */}
                    <button
                      type="button"
                      className="btn p-0 d-flex align-items-center justify-content-center ms-2 flex-shrink-0"
                      onClick={handleSendMessage}
                      disabled={chatLoading || uploading || (!newMessage.trim() && selectedFiles.length === 0 && !selectedDocumentType)}
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        backgroundColor:
                          newMessage.trim() || selectedFiles.length > 0 || selectedDocumentType ? "#c82333" : "#cbd5e1",
                        color: "#ffffff",
                        border: "none",
                        cursor:
                          newMessage.trim() || selectedFiles.length > 0 || selectedDocumentType ? "pointer" : "default",
                        transition: "background-color 0.2s ease",
                      }}
                      title="Send message"
                    >
                      {chatLoading || uploading ? (
                        <div className="spinner-border spinner-border-sm text-white" role="status" style={{ width: 14, height: 14 }}>
                          <span className="visually-hidden">...</span>
                        </div>
                      ) : (
                        <i className="ti ti-send text-white fs-15" style={{ marginLeft: "1px" }}></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Media / Lightbox Preview Modal */}
        {previewMedia && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center"
            style={{ zIndex: 1080, backdropFilter: "blur(4px)" }}
            onClick={() => setPreviewMedia(null)}
          >
            <div
              className="bg-white rounded-2 shadow-lg p-3.5 position-relative"
              style={{ maxWidth: "90vw", maxHeight: "90vh", minWidth: "320px", borderRadius: "8px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                <h6 className="mb-0 fw-bold text-dark text-truncate fs-14" style={{ maxWidth: "70%" }}>
                  <i className="ti ti-photo me-1.5 text-primary"></i>
                  {previewMedia.title}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <a
                    href={previewMedia.url}
                    target="_blank"
                    rel="noreferrer"
                    download={previewMedia.title}
                    className="btn btn-sm btn-outline-primary py-1 px-2.5 fs-12 rounded-2"
                    style={{ borderRadius: "6px" }}
                  >
                    <i className="ti ti-download me-1"></i>Download
                  </a>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setPreviewMedia(null)}
                  ></button>
                </div>
              </div>
              <div className="text-center overflow-auto p-2" style={{ maxHeight: "calc(90vh - 100px)" }}>
                {previewMedia.isImage ? (
                  <img
                    src={previewMedia.url}
                    alt={previewMedia.title}
                    className="img-fluid rounded-2"
                    style={{ maxHeight: "75vh", objectFit: "contain", borderRadius: "6px" }}
                  />
                ) : (
                  <iframe
                    src={previewMedia.url}
                    title={previewMedia.title}
                    style={{ width: "80vw", height: "70vh", border: "none" }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Document Type Selection Dialog (Mobile Screenshot 1) */}
        {showDocumentTypeDialog && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.45)",
              zIndex: 1090,
              backdropFilter: "blur(3px)",
            }}
            onClick={handleCancelDocumentType}
          >
            <div
              className="bg-white shadow-xl p-4 w-100 position-relative"
              style={{
                maxWidth: "380px",
                borderRadius: "20px",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h5 className="fw-bold text-dark mb-1" style={{ fontSize: "18px" }}>
                Select Document Type
              </h5>
              <p className="text-muted fs-12 mb-3" style={{ color: "#94a3b8" }}>
                Which type of document are you requesting from the employee?
              </p>

              <div className="d-flex flex-column gap-2 mb-1">
                {/* Education Documents */}
                <div
                  className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                  style={{
                    borderRadius: "14px",
                    transition: "background-color 0.15s ease",
                    backgroundColor: "#ffffff",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                  onClick={() => {
                    setSelectedDocumentType("Education");
                    setNewMessage("Please share education document");
                    setInputError(null);
                    setShowDocumentTypeDialog(false);
                    setTimeout(() => messageInputRef.current?.focus(), 150);
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        backgroundColor: "#f1f5f9",
                      }}
                    >
                      📚
                    </div>
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                        Education Documents
                      </div>
                      <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                        Certificates, degrees, transcripts, etc.
                      </small>
                    </div>
                  </div>
                  <i className="ti ti-chevron-right text-muted fs-15"></i>
                </div>

                {/* Advertisement Documents */}
                <div
                  className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                  style={{
                    borderRadius: "14px",
                    transition: "background-color 0.15s ease",
                    backgroundColor: "#ffffff",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                  onClick={() => {
                    setSelectedDocumentType("Advertisement");
                    setNewMessage("Please share advertisement document");
                    setInputError(null);
                    setShowDocumentTypeDialog(false);
                    setTimeout(() => messageInputRef.current?.focus(), 150);
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        backgroundColor: "#f1f5f9",
                      }}
                    >
                      📢
                    </div>
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                        Advertisement Documents
                      </div>
                      <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                        Job posting, newspaper clipping etc.
                      </small>
                    </div>
                  </div>
                  <i className="ti ti-chevron-right text-muted fs-15"></i>
                </div>

                {/* Other Documents */}
                <div
                  className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                  style={{
                    borderRadius: "14px",
                    transition: "background-color 0.15s ease",
                    backgroundColor: "#ffffff",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                  onClick={() => {
                    setSelectedDocumentType("Other");
                    setNewMessage("Please share other document");
                    setInputError(null);
                    setShowDocumentTypeDialog(false);
                    setTimeout(() => messageInputRef.current?.focus(), 150);
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        backgroundColor: "#f1f5f9",
                      }}
                    >
                      📄
                    </div>
                    <div>
                      <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                        Other Documents
                      </div>
                      <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                        Any other supporting documents.
                      </small>
                    </div>
                  </div>
                  <i className="ti ti-chevron-right text-muted fs-15"></i>
                </div>

                {/* Horizontal Divider */}
                <div style={{ height: "1px", backgroundColor: "#f1f5f9", margin: "6px 0" }}></div>

                {/* Send Attachment */}
                <div
                  className="d-flex align-items-center justify-content-between p-2.5 cursor-pointer"
                  style={{
                    borderRadius: "14px",
                    transition: "background-color 0.15s ease",
                    backgroundColor: "#ffffff",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(200, 35, 51, 0.04)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
                  onClick={() => {
                    setSelectedDocumentType("");
                    setShowDocumentTypeDialog(false);
                    if (isBoilerplateDocMessage(newMessage)) {
                      setNewMessage("");
                    }
                    setTimeout(() => triggerFileInput(), 150);
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="d-flex align-items-center justify-content-center fs-20 flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        backgroundColor: "rgba(200, 35, 51, 0.1)",
                        color: "#c82333",
                      }}
                    >
                      <i className="ti ti-paperclip" style={{ color: "#c82333" }}></i>
                    </div>
                    <div>
                      <div className="fw-bold" style={{ fontSize: "14px", color: "#c82333" }}>
                        Send Attachment
                      </div>
                      <small className="text-muted d-block" style={{ fontSize: "12px", color: "#94a3b8" }}>
                        Supported: PDF, JPG, PNG
                      </small>
                    </div>
                  </div>
                  <i className="ti ti-chevron-right fs-15" style={{ color: "#c82333" }}></i>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>
          {`
            @keyframes chatSlideIn {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            
            .chat-corner-widget {
              animation: chatSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
            }
            
            @media (max-width: 576px) {
              .chat-corner-widget {
                bottom: 0 !important;
                right: 0 !important;
                left: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                height: 85vh !important;
                max-height: 85vh !important;
                border-radius: 8px 8px 0 0 !important;
              }
            }
            
            .chat-bubble-incoming {
              border-radius: 6px !important;
              border: 1px solid #e2e8f0;
              background-color: #ffffff;
              box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
            }

            .chat-bubble-outgoing {
              border-radius: 6px !important;
              background: linear-gradient(135deg, #f26522 0%, #d94e0c 100%);
              box-shadow: 0 2px 8px rgba(242, 101, 34, 0.22);
            }

            .chat-header-btn:hover {
              background-color: #f1f5f9 !important;
              color: #0f172a !important;
            }

            .chat-attach-btn:hover {
              background-color: #e2e8f0 !important;
            }

            .chat-send-btn:hover:not(:disabled) {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(242, 101, 34, 0.35) !important;
            }

            .chat-input-field:focus {
              background-color: #ffffff !important;
              border-color: #f26522 !important;
              box-shadow: 0 0 0 3px rgba(242, 101, 34, 0.15) !important;
            }

            .chat-messages-container {
              flex: 1;
              overflow-y: auto;
              min-height: 0;
            }
            
            .chat-input-container {
              flex-shrink: 0;
            }
            
            .chat-att-card-in:hover {
              background-color: #f1f5f9 !important;
              border-color: #cbd5e1 !important;
            }

            .chat-att-card-out:hover {
              background-color: rgba(255, 255, 255, 0.26) !important;
              border-color: rgba(255, 255, 255, 0.38) !important;
            }

            .chat-message-content {
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .file-attachment-preview {
              max-width: 100%;
              overflow-x: auto;
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default NOCOutsideJobDetails;
